import { Button } from "#components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { useI18n } from "#hooks/use-i18n"
import { slideUp } from "#lib/animations"
import { cn } from "#lib/utils"
import { motion } from "framer-motion"
import {
  ArrowRight,
  CreditCard,
  LayoutDashboard,
  Settings,
  Sparkles,
  Tag,
  UserRound,
} from "lucide-react"
import { useNavigate } from "react-router-dom"

export function HelpOverview() {
  const navigate = useNavigate()
  const { t } = useI18n()

  const quickLinks = [
    {
      id: "dashboard",
      title: t("pages.help.tabs.dashboard"),
      description: t("pages.help.dashboard.overview.description"),
      icon: LayoutDashboard,
      gradient: "from-blue-500/10 to-cyan-500/10",
    },
    {
      id: "expenses",
      title: t("pages.help.tabs.expenses"),
      description: t("pages.help.expenses.overview.description"),
      icon: CreditCard,
      gradient: "from-purple-500/10 to-pink-500/10",
    },
    {
      id: "categories",
      title: t("pages.help.tabs.categories"),
      description: t("pages.help.categories.overview.description"),
      icon: Tag,
      gradient: "from-green-500/10 to-emerald-500/10",
    },
    {
      id: "partners",
      title: t("pages.help.tabs.partners"),
      description: t("pages.help.partners.overview.description"),
      icon: UserRound,
      gradient: "from-orange-500/10 to-amber-500/10",
    },
    {
      id: "settings",
      title: t("pages.help.tabs.settings"),
      description: t("pages.help.settings.overview.description"),
      icon: Settings,
      gradient: "from-indigo-500/10 to-violet-500/10",
    },
  ]

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={slideUp}
      className="flex flex-1 flex-col gap-6 p-4 md:p-6"
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">{t("pages.help.title")}</h1>
          </div>
          <p className="text-muted-foreground text-lg max-w-2xl">{t("pages.help.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map((link) => {
            const Icon = link.icon
            return (
              <motion.div key={link.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card
                  className="cursor-pointer transition-all hover:shadow-lg border-2 hover:border-primary/50"
                  onClick={() => navigate(`/help/${link.id}`)}
                >
                  <CardHeader className="pb-3">
                    <div
                      className={cn(
                        "w-12 h-12 rounded-lg flex items-center justify-center mb-2 bg-gradient-to-br",
                        link.gradient
                      )}
                    >
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg">{link.title}</CardTitle>
                    <CardDescription className="line-clamp-2 text-sm">
                      {link.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-between group"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/help/${link.id}`)
                      }}
                    >
                      <span>{t("pages.help.learnMore")}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>
      </div>
    </motion.div>
  )
}
